import axios from 'axios';
import Submission from '@/models/submission';
import store from '@/store';
import { applyErrorHandlers } from '@/decorators/applyErrorHandler';

const API_FILE_URL = process.env.VUE_APP_API_URL+"/files";
const API_SUBMISSION_URL = process.env.VUE_APP_API_URL+"/submissions";

export default class SubmissionController {
  static async fetchMySubmissionStatus() {
    const response = await axios.get(`${API_SUBMISSION_URL}/get_my_submission_status`);
    return response.data; // { any_submitted_ids: [...] }
  }

  // Create a new submission
  static async createSubmission(subtaskId, files) {
    const userId = store.getters.userId;
    const teacher = store.getters.isTeacher;
    if (teacher) {
      throw new Error('Teachers cannot submit submissions. Please log in as a student.');
    }

    // Grace period for submission date, so it is properly picked by deadline if submitted in the last second
    const gracePeriod = 1000; // 1 second
    const effectiveTime = new Date(Date.now() - gracePeriod);
    // getTimezoneOffset returns the difference in minutes from local time to UTC (negative for timezones ahead of UTC)
    const timezoneOffset = effectiveTime.getTimezoneOffset() * 60000;
    // adjust the effective time to local time by subtracting the offset
    const localSubmissionTime = new Date(effectiveTime.getTime() - timezoneOffset);
    
    const submissionData = {
      subtask_id: subtaskId,
      student_id: userId,
      submission_date: localSubmissionTime.toISOString().replace('Z', '')
    };

    // Validate submissionData if needed
    Submission.validate(submissionData);

    const semester_id = store.getters.semesterId;
    const formData = new FormData();
    // Send submission_data as JSON string
    formData.append('submission_data', JSON.stringify(submissionData));
    
    // Append files to the same FormData
    files.forEach(file => {
      formData.append('files', file);
    });
    
    const response = await axios.post(
      `${API_FILE_URL}/save_submission_files/${semester_id}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return response;
  }
  // F16a: get precheck log as text (teacher or owner student)
  static async getPrecheckLogContent(submissionId) {
    const response = await axios.get(`${API_SUBMISSION_URL}/precheck_log_content/${submissionId}`);
    return response.data; // { content, filename, truncated }
  }

  // Full precheck log as a file. Goes through axios so the auth token is sent
  // (a plain link would not carry it and the backend answers "Not authenticated").
  static async downloadPrecheckLog(submissionId) {
    return axios.get(`${API_SUBMISSION_URL}/precheck_log_content/${submissionId}`, {
      params: { download: true },
      responseType: 'blob',
    });
  }

  // F13: sanity check state of one submission (teacher or owner student)
  static async getPrecheckStatus(submissionId) {
    const response = await axios.get(`${API_SUBMISSION_URL}/precheck_status/${submissionId}`);
    return response.data; // { submission_id, state, log_available }
  }

  // F16a: get diff of submission vs previous version
  static async getDiffWithPrevious(submissionId) {
    const response = await axios.get(`${API_SUBMISSION_URL}/diff_with_previous/${submissionId}`);
    return response.data; // SubmissionDiffResponse
  }
}

applyErrorHandlers(SubmissionController);